import React, { ReactNode, useCallback, useEffect, useState } from "react";
import { SanityModal, SanityModalContent } from "../../model/common";
import { useModalManager } from "../../utils/useModalManager";
import Modal from "../Modal/Modal";

const openModalEvent = "openmodal";

export const triggerModal = (targetModal: string) => {
  window.dispatchEvent(
    new CustomEvent(openModalEvent, {
      detail: {
        id: targetModal
      }
    })
  );
};

export enum PageModalTypes {
  PowerUpAndPlay = "powerUpAndPlay",
  PowerUpAndPlayWelcomeProgram = "powerUpAndPlayWelcomeProgram",
  PowerUpAndPlayV2 = "powerUpAndPlayV2",
  PowerUpAndPlayV1 = "powerUpAndPlayV1",
  PowerUpAndPlayNoWelcome = "powerUpAndPlayNoWelcome",
  GetCodingDevice = "getCodingDevice",
  GetCodingTransferComputer = "getCodingTransferComputer",
  GetCodingTransferTablet = "getCodingTransferTablet",
  GetCodingYouWillNeedComputer = "getCodingYouWillNeedComputer",
  GetCodingYouWillNeedTablet = "getCodingYouWillNeedTablet",
  AnimationVideo = "animationVideo"
}

export enum PageModalParams {
  TransferCodeHelp = "transferCodeHelp",
  YouWillNeed = "youWillNeed",
  Device = "device"
}

export interface PageModalBodyProps {
  content: SanityModalContent;
  onClose: () => void;
}

interface PageModalProps {
  data: SanityModal;
  onClose?: () => void;
  onOpen?: () => void;
  modalDismissedThisSession?: boolean;
  renderBodyComponent: (
    type: string,
    _rawContent: SanityModalContent | null,
    handleCloseModal: () => void
  ) => ReactNode;
}

const PageModal = ({
  data,
  onClose,
  onOpen,
  modalDismissedThisSession = false,
  renderBodyComponent
}: PageModalProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const { _key, type, showOnPageView, _rawContent } = data;
  const { removeModal, addModal } = useModalManager();

  const handleCloseModal = useCallback(() => {
    setIsOpen(false);
    removeModal(_key);
    if (onClose) {
      onClose();
    }
  }, [_key, onClose, removeModal]);

  const handleOpenModal = useCallback(() => {
    addModal(_key);
    setIsOpen(true);
  }, [_key, addModal]);

  useEffect(() => {
    if (showOnPageView && !modalDismissedThisSession) {
      handleOpenModal();
    }
    if (onOpen) {
      onOpen();
    }
  }, [handleOpenModal, modalDismissedThisSession, onOpen, showOnPageView]);

  useEffect(() => {
    const openModal = (event: Event) => {
      const { id } = (event as CustomEvent).detail;
      if (id === _key) {
        handleOpenModal();
      }
    };
    window.addEventListener(openModalEvent, openModal);
    return () => {
      window.removeEventListener(openModalEvent, openModal);
    };
  }, [_key, addModal, handleOpenModal]);

  return (
    <Modal
      id={data._key}
      isOpen={isOpen}
      title={data.title}
      onClose={handleCloseModal}
      dismissible={data.dismissible}
    >
      {/* Re-rendering modal contents to avoid audio and video playing in the background. */}
      {/* This will also reset selected states on modal close. */}
      {isOpen && renderBodyComponent(type, _rawContent, handleCloseModal)}
    </Modal>
  );
};

export default PageModal;
